import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.syncWithCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.syncWithCart();
    }
  };

  /**
   * Handles the cart update event — fetches the live cart and updates the bubble.
   * @param {Event} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    // Fast path: if the event carries the full cart resource with item_count, use it directly
    const resource = /** @type {any} */ (event).detail?.resource;
    if (resource?.item_count !== undefined) {
      const prevCount = this.currentCartCount;
      this.renderCartBubble(resource.item_count, prevCount);
      return;
    }

    // Fallback: fetch the cart from the server to get the true count
    try {
      const response = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await response.json();
      const prevCount = this.currentCartCount;
      this.renderCartBubble(cart.item_count, prevCount);
    } catch (_) {
      // no-op on network error
    }
  };

  /**
   * Fetches the live cart and syncs the bubble without animation.
   * Called on connectedCallback and pageshow to catch any stale state.
   */
  syncWithCart = async () => {
    try {
      const response = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await response.json();
      this.renderCartBubble(cart.item_count, this.currentCartCount, false);
    } catch (_) {
      // no-op on network error
    }
  };

  /**
   * Renders the cart bubble with the given total item count.
   * @param {number} itemCount - The TOTAL number of items now in the cart.
   * @param {number} prevCount - The count before this update (used to decide animation).
   * @param {boolean} animate - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, prevCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(itemCount),
        timestamp: Date.now(),
      })
    );

    const shouldAnimate = animate && itemCount > 0 && itemCount !== prevCount;
    if (!shouldAnimate) return;

    // Ensure element is visible before starting animation
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}